import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { UserSummary, UserDetail, UserStats } from './userTypes';
import { effectivePremiumTruth } from './entitlementState';

export function useUserManagement() {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name_az' | 'name_za' | 'premium' | 'suspended'>('newest');

  // Detail view
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Suspend modal
  const [suspendModal, setSuspendModal] = useState<{ userId: string; email: string } | null>(null);
  const [suspendDuration, setSuspendDuration] = useState('7d');
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState<{ userId: string; email: string } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Premium toggle
  const [premiumDuration, setPremiumDuration] = useState<number | null>(1);
  const [premiumProcessing, setPremiumProcessing] = useState(false);

  // Password reset
  const [sendingReset, setSendingReset] = useState(false);

  // Risk profile
  const [riskProfile, setRiskProfile] = useState<{ content_reports: number; chat_bans: number; no_shows: number; blocks_received: number; is_suspended: boolean } | null>(null);

  // Notification
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotif = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), type === 'error' ? 30000 : 5000);
  }, []);

  const fetchUsers = useCallback(async (query: string = '') => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_search_users', {
        p_query: query,
        p_limit: 500,
        p_offset: 0,
      });
      if (error) throw error;
      setUsers(data || []);
    } catch (err: any) {
      showNotif(`Failed to load users: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotif]);

  const fetchStats = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_user_stats');
      if (error) throw error;
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => fetchUsers(value), 300);
    setSearchTimeout(timeout);
  };

  // 'verified': detail + entitlement read-back both succeeded and the row
  // matched the requested user. 'partial': detail loaded (grant fields are
  // verified from it) but the entitlement read-back failed or returned no
  // matching row — the paid side is unavailable, NOT confirmed. 'failed':
  // the detail read itself failed.
  const openUserDetail = async (userId: string): Promise<'verified' | 'partial' | 'failed'> => {
    setDetailLoading(true);
    try {
      const [detailRes, riskRes, entitlementRes] = await Promise.all([
        supabase.rpc('admin_get_user_detail', { p_user_id: userId }),
        supabase.rpc('admin_get_user_risk_profile', { p_user_id: userId }),
        // admin_get_user_detail does not return user_entitlements. The
        // admin_search_users (p_query) overload matches au.id exactly and is
        // the deployed committed read-back for tier/status/platform/period.
        supabase.rpc('admin_search_users', { p_query: userId, p_limit: 1, p_offset: 0 }),
      ]);
      if (detailRes.error) throw detailRes.error;
      const detail = detailRes.data as UserDetail;
      const row = !entitlementRes.error && Array.isArray(entitlementRes.data)
        ? (entitlementRes.data as Array<Record<string, any>>).find((r) => r.id === userId)
        : undefined;
      detail.entitlement = row
        ? {
            tier: row.entitlement_tier ?? null,
            status: row.entitlement_status ?? null,
            platform: row.entitlement_platform ?? null,
            period_end: row.entitlement_period_end ?? null,
          }
        : null;
      detail.entitlement_unavailable = !row;
      setSelectedUser(detail);
      setRiskProfile(!riskRes.error && riskRes.data ? riskRes.data : null);
      return row ? 'verified' : 'partial';
    } catch (err: any) {
      showNotif(`Failed to load user: ${err.message}`, 'error');
      // A failed refresh invalidates the previous status for THIS user — the
      // last-known Pro/Free label must not keep presenting as current.
      setSelectedUser((prev) => (prev && prev.id === userId ? { ...prev, status_stale: true } : prev));
      return 'failed';
    } finally {
      setDetailLoading(false);
    }
  };

  const sortedUsers = React.useMemo(() => {
    const sorted = [...users];
    switch (sortBy) {
      case 'newest': sorted.sort((a, b) => new Date(b.signup_date).getTime() - new Date(a.signup_date).getTime()); break;
      case 'oldest': sorted.sort((a, b) => new Date(a.signup_date).getTime() - new Date(b.signup_date).getTime()); break;
      case 'name_az': sorted.sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')); break;
      case 'name_za': sorted.sort((a, b) => (b.display_name || b.email || '').localeCompare(a.display_name || a.email || '')); break;
      case 'premium': sorted.sort((a, b) => (effectivePremiumTruth(b) === 'premium' ? 1 : 0) - (effectivePremiumTruth(a) === 'premium' ? 1 : 0)); break;
      case 'suspended': sorted.sort((a, b) => (isSuspended(b.banned_until) ? 1 : 0) - (isSuspended(a.banned_until) ? 1 : 0)); break;
    }
    return sorted;
  }, [users, sortBy]);

  const handleSuspend = async () => {
    if (!suspendModal || !suspendReason.trim()) {
      showNotif('Reason is required', 'error');
      return;
    }
    setSuspending(true);
    try {
      const { error } = await supabase.rpc('admin_suspend_user', {
        p_user_id: suspendModal.userId,
        p_duration: suspendDuration,
        p_reason: suspendReason.trim(),
      });
      if (error) throw error;
      showNotif(`Suspended ${suspendModal.email} for ${suspendDuration}`, 'success');
      setSuspendModal(null);
      setSuspendReason('');
      fetchUsers(searchQuery);
      if (selectedUser?.id === suspendModal.userId) openUserDetail(suspendModal.userId);
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    } finally {
      setSuspending(false);
    }
  };

  const handleUnsuspend = async (userId: string, email: string) => {
    try {
      const { error } = await supabase.rpc('admin_unsuspend_user', { p_user_id: userId });
      if (error) throw error;
      showNotif(`Unsuspended ${email}`, 'success');
      fetchUsers(searchQuery);
      if (selectedUser?.id === userId) openUserDetail(userId);
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal || !deleteReason.trim()) {
      showNotif('Reason is required', 'error');
      return;
    }
    if (deleteConfirmEmail !== deleteModal.email) {
      showNotif('Email confirmation does not match', 'error');
      return;
    }
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('admin_delete_user_gdpr', {
        p_user_id: deleteModal.userId,
        p_reason: deleteReason.trim(),
        p_confirm_email: deleteConfirmEmail,
      });
      if (error) throw error;
      showNotif(`Deleted ${deleteModal.email} (GDPR compliant)`, 'success');
      setDeleteModal(null);
      setDeleteReason('');
      setDeleteConfirmEmail('');
      setSelectedUser(null);
      fetchUsers(searchQuery);
      fetchStats();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handlePremiumToggle = async (userId: string, email: string, enable: boolean) => {
    setPremiumProcessing(true);
    try {
      const { data, error } = await supabase.rpc('admin_set_premium_override', {
        target_user_id: userId,
        enabled: enable,
        duration_months: enable ? premiumDuration : null,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string; premium_override?: boolean; premium_override_expires_at?: string };
      if (!result.success) throw new Error(result.error || 'Failed');
      // Re-read committed effective state BEFORE reporting success: the
      // mutation response proves the write, the read-back proves the status.
      const outcome = await openUserDetail(userId);
      fetchStats();
      if (outcome === 'failed') {
        showNotif(
          `${enable ? 'Grant' : 'Revoke'} committed for ${email}, but the status refresh failed — displayed status may be stale. Verify before relying on it.`,
          'error'
        );
        return;
      }
      const committedText = enable
        ? result.premium_override_expires_at
          ? `Premium granted to ${email} until ${formatDate(result.premium_override_expires_at)}`
          : `Premium granted to ${email} (permanent)`
        : `Premium revoked from ${email}`;
      if (outcome === 'partial') {
        // The grant fields were verified from the detail read; the paid
        // entitlement could not be — a committed success, not a verified
        // effective status.
        showNotif(`${committedText} — grant verified; paid entitlement could not be verified (shown as unavailable).`, 'error');
        return;
      }
      showNotif(committedText, 'success');
    } catch (err: any) {
      showNotif(`Premium update failed: ${err.message}`, 'error');
    } finally {
      setPremiumProcessing(false);
    }
  };

  const handleSendPasswordReset = async (email: string) => {
    setSendingReset(true);
    try {
      const redirectUrl = `${window.location.origin}/auth/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
      if (error) throw error;
      showNotif(`Password reset link sent to ${email}`, 'success');
    } catch (err: any) {
      showNotif(`Failed to send reset link: ${err.message}`, 'error');
    } finally {
      setSendingReset(false);
    }
  };

  const isSuspended = (bannedUntil: string | null) => {
    if (!bannedUntil) return false;
    return new Date(bannedUntil) > new Date();
  };

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : 'Never';
  const formatDateTime = (d: string | null) => d ? new Date(d).toLocaleString() : 'Never';

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'var(--rp-text)',
  };

  return {
    users, stats, loading, searchQuery, sortBy, setSortBy, selectedUser, detailLoading,
    suspendModal, setSuspendModal, suspendDuration, setSuspendDuration, suspendReason,
    setSuspendReason, suspending, deleteModal, setDeleteModal, deleteReason, setDeleteReason,
    deleteConfirmEmail, setDeleteConfirmEmail, deleting, premiumDuration, setPremiumDuration,
    premiumProcessing, sendingReset, riskProfile, notification, setNotification,
    fetchUsers, fetchStats, handleSearch, openUserDetail, sortedUsers, handleSuspend,
    handleUnsuspend, handleDelete, handlePremiumToggle, handleSendPasswordReset,
    isSuspended, formatDate, formatDateTime, inputStyle,
  };
}

export type UserModel = ReturnType<typeof useUserManagement>;
